"use client";
import { useState, useMemo } from "react";
import { useAllFreeCourses } from "@/api/hook/useFreeCourses";
import CourseCard from "@/components/landing/CourseCard";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { useRouter } from "next/navigation";

const PAGE_SIZE = 30;

export default function FreeCoursesPage() {
  const router = useRouter();
  const { data: courses, isLoading, error } = useAllFreeCourses();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);

  // Derive unique categories from the fully-free courses
  const categories = useMemo(() => {
    if (!courses) return [];
    const seen = new Map<string, string>();
    courses.forEach((c) => {
      if (c.categoryName) seen.set(c.categoryName, c.categoryName);
    });
    return Array.from(seen.keys());
  }, [courses]);

  // Filter
  const filtered = useMemo(() => {
    if (!courses) return [];
    return courses.filter((c) => {
      const matchesSearch =
        searchTerm.length < 2 ||
        c.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.courseDescription.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" || c.categoryName === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [courses, searchTerm, selectedCategory]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (val: string) => {
    setSearchTerm(val);
    setPage(1);
  };

  const handleCategory = (val: string) => {
    setSelectedCategory(val);
    setPage(1);
  };

  return (
    <>
      <Header />

      {/* ── Hero Banner ─────────────────────────────────────────────────── */}
      <div className="relative bg-[#21205A] overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#B97BFF]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#EFAE33]/20 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="relative container mx-auto px-4 pt-28 pb-20 text-center">
          <button
            onClick={() => router.push("/")}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm font-medium mb-8 mx-auto"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </button>

          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EFAE33] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EFAE33]" />
            </span>
            <span className="text-xs font-bold text-white/80 tracking-widest uppercase">
              100% Free · Course &amp; Certificate
            </span>
          </div>

          <h1 className="font-bold text-white text-2xl md:text-4xl lg:text-5xl leading-tight mb-4">
            Completely Free{" "}
            <span
              style={{
                backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Courses &amp; Certificates
            </span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10">
            Every course here is{" "}
            <span className="text-[#EFAE33] font-semibold">free to enroll</span>{" "}
            and comes with a{" "}
            <span className="text-[#B97BFF] font-semibold">
              free internationally recognised certificate
            </span>
            . No hidden fees.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            {[
              {
                value: isLoading ? "—" : `${courses?.length ?? 0}+`,
                label: "Free Courses",
              },
              { value: "100%", label: "Free Certificates" },
              { value: "18+", label: "Countries Reached" },
            ].map((s) => (
              <div
                key={s.label}
                className="text-center bg-white/10 border border-white/15 backdrop-blur-sm rounded-2xl px-8 py-4"
              >
                <p className="text-2xl md:text-3xl font-bold text-white">
                  {s.value}
                </p>
                <p className="text-white/60 text-sm font-medium mt-0.5">
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search free courses..."
              className="w-full rounded-xl py-4 pl-12 pr-6 text-base font-medium text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B97BFF]/50 shadow-xl"
            />
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <HiMagnifyingGlass className="h-5 w-5 text-[#8253C3]" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-4">
            <select
              value={selectedCategory}
              onChange={(e) => handleCategory(e.target.value)}
              className="text-sm font-semibold text-gray-700 bg-gray-100 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#8253C3]/30 cursor-pointer"
            >
              <option value="all">All Categories</option>
              {categories.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <span className="ml-auto text-sm text-gray-400 font-medium">
              {isLoading
                ? "Loading..."
                : `${filtered.length} course${filtered.length !== 1 ? "s" : ""} found`}
            </span>
          </div>
        </div>
      </div>

      {/* ── Course Grid ─────────────────────────────────────────────────── */}
      <div className="bg-[#F4F6F9] min-h-[60vh]">
        <div className="container mx-auto px-4 py-12">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <div className="flex gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-3 h-3 rounded-full bg-[#EFAE33] animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <p className="text-gray-400 text-sm font-medium">
                Loading free courses...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className="text-red-400 font-semibold">
                Failed to load courses. Please try again.
              </p>
            </div>
          ) : paginated.length === 0 ? (
            <div className="text-center py-32">
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-gray-500 text-lg font-semibold">
                No courses match your search.
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Try adjusting your filters or search term.
              </p>
            </div>
          ) : (
            <>
              {/* 3-column grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginated.map((course) => (
                  <CourseCard key={course.id} data={course} />
                ))}
              </div>

              {/* ── Pagination ─────────────────────────────────────────── */}
              {totalPages > 1 && (
                <div className="flex flex-wrap justify-center items-center gap-2 mt-12">
                  {/* Prev */}
                  <button
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={page === 1}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Prev
                  </button>

                  {/* Page numbers with ellipsis */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (n) =>
                        n === 1 || n === totalPages || Math.abs(n - page) <= 2,
                    )
                    .reduce<(number | "...")[]>((acc, n, idx, arr) => {
                      if (idx > 0 && n - (arr[idx - 1] as number) > 1)
                        acc.push("...");
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => {
                            setPage(item as number);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                            page === item
                              ? "text-white shadow-md scale-110"
                              : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                          style={
                            page === item
                              ? {
                                  background:
                                    "linear-gradient(135deg, #21205A, #8253C3)",
                                }
                              : {}
                          }
                        >
                          {item}
                        </button>
                      ),
                    )}

                  {/* Next */}
                  <button
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={page === totalPages}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Page info */}
              <p className="text-center text-sm text-gray-400 mt-4">
                Page {page} of {totalPages}&nbsp;·&nbsp;{filtered.length} total
                courses
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── CTA Banner ──────────────────────────────────────────────────── */}
      <div className="bg-[#21205A] py-14">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-white font-bold text-2xl md:text-3xl mb-3">
            Ready to start your free learning journey?
          </h3>
          <p className="text-white/60 text-base mb-8 max-w-lg mx-auto">
            Create your free account and enroll in any of these courses today —
            course and certificate, both on us.
          </p>
          <a
            href="https://student.laplagemetaverse.org/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base shadow-lg hover:scale-105 transition-transform"
            style={{ background: "linear-gradient(135deg, #EFAE33, #B97BFF)" }}
          >
            <span>✦</span>
            Create Free Account
          </a>
        </div>
      </div>

      <Footer />
    </>
  );
}
